// SlateEditor.tsx
import React, { useCallback, useMemo, useState } from "react";
import { Slate, Editable, withReact } from "slate-react";
import { createEditor, Descendant, Editor, Transforms, Element as SlateElement } from "slate";
import styled from "styled-components";
import { BsTextLeft, BsTextCenter, BsTextRight } from "react-icons/bs";
import { LinkElement } from "../../types/editor";
import { serializeToHTML } from "../../utils/serializeToHTML";
import { deserializeHTML } from "../../utils/deserializeHTML";
import { fileApiService } from "../../api/apiService";
import { useAlertStore } from "../../store/alertStore";
import { postFiles } from "../../api/files";
import { withImages } from "./withImages";
import LinkModal from "./LinkModal";
import config from "../../config";

interface SlateEditorProps {
  setContent: (val: string) => void;
  initialHtml?: string;
}

// 이미지 노드 타입
type ImageElement = {
  type: "image";
  url: string;
  children: { text: "" }[];
};

// 툴바 버튼 컴포넌트
const ToolbarButton = (props: {
  label: string;
  onClick: () => void;
  style?: React.CSSProperties;
}) => (
  <button
    onMouseDown={(e) => {
      e.preventDefault();
      props.onClick();
    }}
    style={{
      height: 30,
      padding: "5px 8px",
      cursor: "pointer",
      border: "1px solid #ccc",
      borderRadius: 4,
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...props.style,
    }}
  >
    {props.label !== "왼쪽" && props.label !== "중앙" && props.label !== "오른쪽" && props.label}
    {props.label === "왼쪽" && <BsTextLeft />}
    {props.label === "중앙" && <BsTextCenter />}
    {props.label === "오른쪽" && <BsTextRight />}
  </button>
);

export default function SlateEditor({ setContent, initialHtml }: SlateEditorProps) {
  const setAlert = useAlertStore((state) => state.setAlert);
  const [showLinkModal, setShowLinkModal] = useState(false);

  // 초기 값
  const initialValue = useMemo<Descendant[]>(() => {
    if (initialHtml) {
      return deserializeHTML(initialHtml);
    } else {
      return [
        {
          type: "paragraph",
          children: [{ text: "" }],
        },
      ];
    }
  }, [initialHtml]);

  // ★ 에디터 생성 시 withImages 적용
  const editor = useMemo(() => {
    // 순서 주의: withImages → withReact → createEditor
    return withImages(withReact(createEditor()));
  }, []);

  const insertImage = useCallback(
    (url: string) => {
      const imageNode: ImageElement = {
        type: "image",
        // 서버 경로 + url 연결
        url: config.front.url + url,
        children: [{ text: "" }],
      };
      Transforms.insertNodes(editor, imageNode as any);
    },
    [editor]
  );

  // 파일 업로드
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];

    try {
      const formData = new FormData();
      formData.append("file", file, "[PROXY]");
      formData.append("file_path", "notice");
      formData.append("file_type", "1");
      formData.append("permit_level", "0");

      const filesResponse = await fileApiService.post<any>("/files/post.php", formData);

      if (filesResponse.statusCode === 200) {
        const filesData = filesResponse.data;
        const postFilesResponse = await postFiles(filesData);

        if (postFilesResponse.statusCode === 200) {
          const serverImagePath = postFilesResponse.data.file_url;
          insertImage(serverImagePath);
        } else {
          setAlert("파일 업로드에 실패했습니다. 다시 시도해주세요.", "red");
        }
      } else {
        setAlert("파일 업로드에 실패했습니다. 다시 시도해주세요.", "red");
      }
    } catch (error) {
      console.error(error);
      setAlert("업로드 중 오류가 발생했습니다. 다시 시도해주세요.", "red");
    }
  };

  // 링크 추가/제거 함수
  const toggleLink = () => {
    const isLinkActive = isLinkEnabled();
    if (isLinkActive) {
      // 링크 제거
      Transforms.unwrapNodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === "link",
      });
      Transforms.setNodes(editor, { type: "paragraph" }, {
        match: (n) => SlateElement.isElement(n),
      });
    } else {
      // 모달 열기
      setShowLinkModal(true);
    }
  };

  // 모달에서 "확인" 시 링크 삽입
  const handleConfirmLink = (url: string) => {
    setShowLinkModal(false);

    // url이 비어있으면 아무것도 안 함
    if (!url) return;

    // 링크 추가
    const linkNode: LinkElement = {
      type: "link",
      url,
      children: [{ text: "링크 텍스트를 입력하세요." }],
    };
    Transforms.wrapNodes(editor, linkNode, { split: true });
    Transforms.collapse(editor, { edge: "end" });
  };

  // 텍스트 스타일 토글 함수
  const toggleMark = (format: string, value: string | boolean) => {
    const marks = Editor.marks(editor) as Record<string, any> | null;
    const isActive = marks ? marks[format] === value : false;

    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, value);
    }
  };

  // 블록 정렬
  function setTextAlign(align: "left" | "center" | "right") {
    Transforms.setNodes(
      editor,
      { textAlign: align },
      { match: (n) => SlateElement.isElement(n), split: true }
    );
  }

  const setFontSize = (size: string) => {
    toggleMark("fontSize", size);
  };

  const setTextColor = (color: string) => {
    toggleMark("color", color);
  };

  const isLinkEnabled = () => {
    const [match] = Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === "link",
    });
    return !!match;
  };

  // onChange 시 HTML 직렬화
  const handleChange = (val: Descendant[]) => {
    const html = serializeToHTML(val);
    setContent(html);
  };

  return (
    <>
      <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
        <Toolbar>
          {/* 폰트 크기 */}
          <select
            onChange={(e) => setFontSize(e.target.value)}
            defaultValue="16px"
            style={{
              padding: "5px 8px",
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          >
            <option value="14pt">14pt</option>
            <option value="16pt">16pt</option>
            <option value="18pt">18pt</option>
            <option value="22pt">22pt</option>
            <option value="26pt">26pt</option>
            <option value="30pt">30pt</option>
            <option value="36pt">36pt</option>
            <option value="42pt">42pt</option>
            <option value="50pt">50pt</option>
          </select>

          {/* 굵게 */}
          <ToolbarButton
            label="B"
            onClick={() => toggleMark("bold", true)}
            style={{ fontWeight: "bold" }}
          />

          {/* 텍스트 색상 */}
          <label>
            <input
              type="color"
              onChange={(e) => setTextColor(e.target.value)}
              style={{ cursor: "pointer" }}
            />
          </label>

          {/* 밑줄 */}
          <ToolbarButton
            label="U"
            onClick={() => toggleMark("underline", true)}
            style={{ textDecoration: "underline" }}
          />

          {/* 정렬 */}
          <ToolbarButton label="왼쪽" onClick={() => setTextAlign("left")} style={{ textAlign: "left" }} />
          <ToolbarButton label="중앙" onClick={() => setTextAlign("center")} style={{ textAlign: "center" }} />
          <ToolbarButton label="오른쪽" onClick={() => setTextAlign("right")} style={{ textAlign: "right" }} />

          {/* 링크 */}
          <ToolbarButton label="링크" onClick={toggleLink} style={{ textDecoration: "underline" }} />

          {/* 이미지 업로드 */}
          <input
            type="file"
            accept="image/*"
            id="image-upload"
            style={{ display: "none" }}
            onChange={handleImageUpload}
          />
          <ToolbarButton
            label="이미지"
            onClick={() => document.getElementById("image-upload")?.click()}
          />
        </Toolbar>

        {/* 에디터 */}
        <Editable
          renderElement={(props) => <Element {...props} />}
          renderLeaf={(props) => <Leaf {...props} />}
          placeholder="내용을 입력하세요."
          style={{
            fontSize: "14pt",
            maxWidth: 1000,
            height: "calc(100% - 47px)",
            padding: 16,
            lineHeight: 1.2,
            border: "1px solid #ccc",
            borderRadius: "0 0 4px 4px",
            display: "block",
            overflowY: "auto",
            whiteSpace: "pre-wrap",
          }}
        />
      </Slate>

      <LinkModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onConfirm={handleConfirmLink}
      />
    </>
  );
}

// Element 컴포넌트
const Element = ({ attributes, children, element }: any) => {
  switch (element.type) {
    case "link":
      return (
        <a
          {...attributes}
          href={element.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "blue", textDecoration: "underline" }}
        >
          {children}
        </a>
      );

    case "image":
      // Void Node인 이미지 렌더링
      return (
        <div {...attributes}>
          {/* contentEditable={false}를 지정하면 텍스트 커서가 들어가지 않도록 막을 수 있음 */}
          <div contentEditable={false}>
            <img
              src={element.url}
              alt="uploaded"
              style={{ maxWidth: "100%", display: "block", margin: "8px 0" }}
            />
          </div>
          {children}
        </div>
      );

    default:
      // paragraph
      const blockStyle: React.CSSProperties = {};
      if (element.textAlign) {
        blockStyle.textAlign = element.textAlign;
      }
      return (
        <p {...attributes} style={blockStyle}>
          {children}
        </p>
      );
  }
};

// Leaf 컴포넌트
const Leaf = ({ attributes, children, leaf }: any) => {
  let style: React.CSSProperties = {};

  if (leaf.bold) {
    style.fontWeight = "bold";
  }
  if (leaf.underline) {
    style.textDecoration = "underline";
  }
  if (leaf.color) {
    style.color = leaf.color;
  }
  if (leaf.fontSize) {
    style.fontSize = leaf.fontSize;
  }

  return (
    <span {...attributes} style={style}>
      {children}
    </span>
  );
};

const Toolbar = styled.div`
  max-width: 1000px;
  border: 1px solid #ccc;
  border-bottom: none;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
